import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Breadcrumb, Card } from 'react-bootstrap';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import './ArticleView.scss';

/**
 * ArticleView component - Displays a single knowledge center article
 * 
 * @returns {JSX.Element} The ArticleView component
 */
const ArticleView = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState([]);
  
  /**
   * Navigate to homepage and scroll to contact section
   */
  const navigateToContactSection = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  useEffect(() => {
    const fetchArticleData = async () => {
      try {
        // Load articles.json
        const articlesPath = `${process.env.PUBLIC_URL}/knowledge-center/articles.json`;
        const articlesResponse = await fetch(articlesPath);
        const articlesData = await articlesResponse.json();
        
        const foundArticle = articlesData.articles.find(a => a.id === articleId);
        
        if (!foundArticle) {
          setNotFound(true);
          return;
        }
        
        setArticle(foundArticle);
        
        // Find related articles (articles with matching tags)
        const findRelatedArticles = (currentArticle, allArticles) => {
          // Filter out the current article and find others that share at least one tag
          return allArticles
            .filter(a => a.id !== currentArticle.id)
            .filter(a => a.tags.some(tag => currentArticle.tags.includes(tag)))
            .sort((a, b) => {
              // Count matching tags to sort by relevance
              const matchingTagsA = a.tags.filter(tag => currentArticle.tags.includes(tag)).length;
              const matchingTagsB = b.tags.filter(tag => currentArticle.tags.includes(tag)).length;
              // Sort by number of matching tags (descending)
              return matchingTagsB - matchingTagsA;
            })
            .slice(0, 5); // Limit to 5 related articles
        };
        
        const related = findRelatedArticles(foundArticle, articlesData.articles);
        setRelatedArticles(related);
        
        // Load article content from markdown file
        try {
          // Create the path to the markdown file
          const markdownPath = `${process.env.PUBLIC_URL}/knowledge-center/${foundArticle.contentFile}`;
          
          // Fetch the raw content
          const response = await fetch(markdownPath);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch markdown content: ${response.status} ${response.statusText}`);
          }
          
          const markdownContent = await response.text();
          
          // Check if we actually got content
          if (markdownContent.trim() === '') {
            throw new Error('Markdown content is empty');
          }
          
          setContent(markdownContent);
          setLoading(false);
        } catch (contentError) {
          console.error("Error loading article content:", contentError);
          setError("Could not load article content. Please try again later.");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading article data:", err);
        setError("Failed to load article information. Please try again later.");
        setLoading(false);
      }
    };

    window.scrollTo(0, 0);
    fetchArticleData();
  }, [articleId]);

  // Format date to display in a readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (notFound) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <div className="article-view-page">
      <div className="article-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + article.image})` }}>
        <div className="hero-overlay"></div>
        <Container>
          <div className="article-header">
            <div className="title-container">
              <h1 className="article-title">{article.title}</h1>
            </div>
            <div className="article-meta">
              <div className="meta-item">
                <FontAwesomeIcon icon={faCalendarAlt} className="meta-icon" />
                <span>{formatDate(article.datePublished)}</span>
              </div>
              <div className="meta-item">
                <FontAwesomeIcon icon={faUser} className="meta-icon" />
                <span>{article.author}</span>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container className="article-container">
        <Row>
          <Col lg={8}>
            <Card className="article-content-card">
              <Card.Body>
                <div className="markdown-content markdown-body">
                  {/* Use ReactMarkdown with content as children instead of a prop */}
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    components={{
                      h1: ({node, ...props}) => <h2 className="article-heading-1" {...props} />,
                      h2: ({node, ...props}) => <h3 className="article-heading-2" {...props} />,
                      h3: ({node, ...props}) => <h4 className="article-heading-3" {...props} />,
                      h4: ({node, ...props}) => <h5 className="article-heading-4" {...props} />,
                      h5: ({node, ...props}) => <h6 className="article-heading-5" {...props} />,
                      img: ({node, ...props}) => {
                        const src = props.src;
                        // Handle different image path patterns
                        let imgSrc = src;
                        
                        // If path starts with 'images/' or '/images/'
                        if (src.startsWith('images/') || src.startsWith('/images/')) {
                          imgSrc = `${process.env.PUBLIC_URL}/${src.replace(/^\//, '')}`;
                        }
                        // If path starts with 'knowledge-center/'
                        else if (src.startsWith('knowledge-center/')) {
                          imgSrc = `${process.env.PUBLIC_URL}/${src}`;
                        }
                        
                        return <img src={imgSrc} alt={props.alt || ''} className="img-fluid article-image" />;
                      },
                      table: ({node, ...props}) => (
                        <div className="table-responsive">
                          <table className="table table-bordered" {...props} />
                        </div>
                      ),
                      code: ({node, inline, className, children, ...props}) => {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <pre className={`language-${match[1]}`}>
                            <code className={`language-${match[1]}`} {...props}>
                              {String(children).replace(/\n$/, '')}
                            </code>
                          </pre>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
              </Card.Body>
            </Card>

            <div className="back-link mt-4">
              <Link to="/resources" className="btn btn-outline-primary">
                <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
                Back to Resources
              </Link>
            </div>
          </Col>
          
          <Col lg={4}>
            <div className="sidebar-container">
              <Card className="sidebar-card mb-4">
                <Card.Body>
                  <h4>Related Articles</h4>
                  <hr />
                  {relatedArticles.length > 0 ? (
                    <ul className="related-articles-list">
                      {relatedArticles.map(relatedArticle => (
                        <li key={relatedArticle.id}>
                          <Link to={`/knowledge/${relatedArticle.id}`}>
                            {relatedArticle.title}
                          </Link>
                          <div className="matching-tags">
                            {relatedArticle.tags
                              .filter(tag => article.tags.includes(tag))
                              .slice(0, 2)
                              .map((tag, idx) => (
                                <span key={idx} className="matching-tag">#{tag}</span>
                              ))}
                            {relatedArticle.tags.filter(tag => article.tags.includes(tag)).length > 2 && 
                              <span className="matching-tag">+{relatedArticle.tags.filter(tag => article.tags.includes(tag)).length - 2} more</span>
                            }
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-related-articles">
                      No related articles found with matching topics.
                    </p>
                  )}
                </Card.Body>
              </Card>

              <Card className="sidebar-card">
                <Card.Body>
                  <h4>Need Assistance?</h4>
                  <hr />
                  <p>Our support team is available to help you with any questions or issues.</p>
                  <button onClick={navigateToContactSection} className="btn btn-primary w-100">Contact Support</button>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ArticleView;
